import Cookies from 'js-cookie';
import { INLINE_ENROLLMENT_DUPLICATE_EMAIL, INLINE_ENROLLMENT_URL, HTTP_SUCCESS_CODE } from '../constants';
import { encryptPassword } from './encrypt';
import { ContactInfoFormData } from '../organisms/ContactInfo/ContactInfo.types';
import { BillingAndContactFormData } from '../organisms/BillingAndContact/BillingAndContact.types';

export type LoginPayload = {
  userId: string;
  password: string;
  returnUrl: string;
  rememberMe?: boolean;
};

type UserDetails = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  pwrd: string;
  confirmPwrd: string;
  rememberMe: boolean;
  countryCode: string;
  postalCode: string;
};

type UserConsent = {
  code: 'CORE_REQUIRED_CONSENT' | 'PERSONALIZED_MARKETING_CONSENT';
  optIn: boolean;
};

type CommunicationPreference = {
  type: 'subscriptionsOffers' | 'subscriptions3rdParty';
  optIn: boolean;
};

export type EnrollUserPayload = {
  userDetails: UserDetails;
  userConsents: UserConsent[];
  communicationPreferences: CommunicationPreference[];
};

export const login = async (signInUrl: string, payload: LoginPayload): Promise<Response> => {
  return fetch(signInUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

export const logout = async (url: string): Promise<Response> => {
  return fetch(url);
};

export const getRememberedUserDetails = async (url: string) => {
  return fetch(url);
};

export const clearRememberedUserDetails = async (url: string): Promise<Response> => {
  return fetch(url);
};

export const enrollUser = async (data: EnrollUserPayload) => {
  let rewardId = '';
  let isDuplicateEmailError = false;

  try {
    const response = await fetch(INLINE_ENROLLMENT_URL, {
      method: 'POST',
      headers: {
        Cookie: Cookies.get('sessionID')?.toString() ?? '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.status === HTTP_SUCCESS_CODE) {
      const result = await response.json();
      rewardId = result?.customerDetails?.rewardsId;
    }
  } catch (error: unknown) {
    const enrollError = error as { response?: { data?: { message: string } } };
    isDuplicateEmailError = enrollError?.response?.data?.message === INLINE_ENROLLMENT_DUPLICATE_EMAIL;
  }

  return { rewardId, isDuplicateEmailError };
};

export const getEnrollUserPayload = (
  contactData: BillingAndContactFormData | ContactInfoFormData
): EnrollUserPayload => {
  const { userInfo, inlineEnrolment, addressInfo } = contactData;
  const { firstName, lastName, email } = userInfo;
  const { createPassword, confirmPassword, rememberMe, receiveOffers, receiveUpdates } = inlineEnrolment;
  const { zipcode, country } = addressInfo;

  return {
    userDetails: {
      firstName,
      lastName,
      emailAddress: email,
      pwrd: encryptPassword(createPassword),
      confirmPwrd: encryptPassword(confirmPassword),
      rememberMe,
      countryCode: country.code,
      postalCode: zipcode,
    },
    userConsents: [
      { code: 'CORE_REQUIRED_CONSENT', optIn: false },
      { code: 'PERSONALIZED_MARKETING_CONSENT', optIn: false },
    ],
    communicationPreferences: [
      { type: 'subscriptionsOffers', optIn: receiveOffers },
      { type: 'subscriptions3rdParty', optIn: receiveUpdates },
    ],
  };
};
